import { Injectable } from '@angular/core';

const DEVICE_ID_KEY = 'saltwaterup.device_id';

/**
 * Manages a stable per-install device identifier, mirroring the iOS app's
 * `Config.device_id`. In the iOS app this was generated once on first
 * launch and stored in the local SQLite DB's `configs` row.
 *
 * Storage strategy: plain localStorage. We previously used Capacitor's
 * Preferences plugin, but its web implementation is lazy-loaded — and
 * that dynamic import fails offline in dev mode (Vite serves chunks
 * over HTTP) and triggers a "Failed to fetch dynamically imported
 * module" error that propagates up through createCatch. localStorage
 * is synchronous, has no lazy-load hop, and is what Preferences-web
 * uses under the hood anyway.
 *
 * If we ever ship as a Capacitor native app (iOS/Android shell), we'd
 * want native secure storage rather than localStorage — but the PWA
 * doesn't need that, and the device_id is non-sensitive (just a per-
 * install opaque identifier).
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdService {
  private cachedId: string | null = null;

  async getOrCreate(): Promise<string> {
    if (this.cachedId) {
      return this.cachedId;
    }

    // Synchronous reads — kept inside an async function so callers
    // that already use it as a Promise don't need to change.
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(DEVICE_ID_KEY);

      // Migration: the previous implementation used Capacitor's
      // Preferences plugin, which prefixes its keys with
      // "CapacitorStorage.". Existing users have their device_id under
      // that prefix; copy it to the raw key on first encounter so they
      // keep the same id (server-side records are tied to it). The
      // old key is left in place — harmless, costs nothing, and means
      // we don't re-migrate if this code path runs again.
      if (!stored) {
        const legacy = localStorage.getItem(`CapacitorStorage.${DEVICE_ID_KEY}`);
        if (legacy) {
          localStorage.setItem(DEVICE_ID_KEY, legacy);
          stored = legacy;
        }
      }
    } catch {
      // localStorage can throw in private-browsing mode on some
      // older browsers; we fall through to generating a fresh id
      // (which won't persist, but the service-instance cache will
      // hold it for the page lifetime).
    }

    if (stored) {
      this.cachedId = stored;
      return stored;
    }

    const newId = this.generateUuid();
    try {
      localStorage.setItem(DEVICE_ID_KEY, newId);
    } catch {
      /* same private-browsing fallthrough */
    }
    this.cachedId = newId;
    return newId;
  }

  /** Test hook - not used in normal flow. */
  async reset(): Promise<void> {
    this.cachedId = null;
    try {
      localStorage.removeItem(DEVICE_ID_KEY);
    } catch {
      /* ignore */
    }
  }

  private generateUuid(): string {
    // Prefer crypto.randomUUID when available (modern browsers, secure
    // contexts). Fall back to a manually constructed v4 UUID for any
    // environment that lacks it (e.g. older WebViews).
    //
    // We capture `crypto` into a local of type `Crypto` to keep TS from
    // narrowing it away after the `'randomUUID' in crypto` test — the
    // narrowing logic in newer TS versions concludes that if randomUUID
    // is the only known property and it isn't there, then nothing else
    // is either. Holding the broader type locally sidesteps that.
    const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
    if (!c) {
      // No crypto at all — vanishingly rare in any modern browser, but
      // we handle it cleanly rather than throwing. Math.random isn't
      // cryptographically strong but it's good enough for a device id.
      return this.weakUuid();
    }

    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    // Per RFC 4122 §4.4: set version (4) and variant bits. The
    // non-null assertions are safe because Uint8Array(16) always has
    // 16 elements; TS strict mode flags indexed access regardless.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }

  /** Math.random-based UUID-v4 fallback. Last-resort path only. */
  private weakUuid(): string {
    const hex = Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 256).toString(16).padStart(2, '0'),
    );
    // Set version + variant bits to keep the format valid.
    const v6 = (parseInt(hex[6]!, 16) & 0x0f) | 0x40;
    const v8 = (parseInt(hex[8]!, 16) & 0x3f) | 0x80;
    hex[6] = v6.toString(16).padStart(2, '0');
    hex[8] = v8.toString(16).padStart(2, '0');
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }
}
